import classNames from 'classnames';
import config from 'config';
import type { Transition } from 'framer-motion';
import { motion } from 'framer-motion';
import React, { useMemo } from 'react';
import styles from './ProgressBar.module.scss';

interface Props extends Omit<React.HTMLProps<HTMLDivElement>, 'size'> {
  /**
   * Animate the progress bar on mount.
   * @default false
   */
  animated?: boolean;

  /**
   * Override the default background color.
   */
  backgroundColor?: string;

  /**
   * Minimum width of the progress bar in pixels.
   * @default 0
   */
  minWidth?: number;

  /**
   * Clamp the minimum progress so that there is a visual indicator for even microscopic values, as
   * long as they are non-zero. Note: If disabled, and rounded corners are enabled, the progress bar
   * will look squished when the values become small enough.
   * @default true
   */
  nonZeroMinimumProgress?: boolean;

  /**
   * Override the default progress bar color.
   */
  progressColor?: string;
  /**
   * Override the default secondary progress bar color.
   */
  secondaryProgressColor?: string;

  /**
   * Round the corners of the progress bar.
   * @default true
   */
  rounded?: boolean;

  /**
   * If true, the progress bar grows from right to left.
   * @default false
   */
  rtl?: boolean;

  /**
   * Thickness of the progress bar.
   * @default 'medium'
   */
  size?: 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large';

  /**
   * Current progress value, in percent. Negative values are clamped to 0.
   * @default 0
   */
  value?: number;
  /**
   * Secondary progress value, in percent. Negative values are clamped to 0.
   * @default 0
   */
  secondaryValue?: number;
}

const ExcessCutoff = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg width="5" height="12" viewBox="0 0 5 12" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.52579 0L3.84528 1.48679L2.63898 2.84604L4.50502 4.94868L2.63898 7.05132L4.50502 9.15396L1.97924 12L0.659745 10.5132L1.86604 9.15396L0 7.05132L1.86604 4.94868L0 2.84604L2.52579 0Z"
    />
  </svg>
);

const barTransition: Transition = {
  // Double the duration as it looks jarring when the animation is too fast for wide bars
  duration: config.animation.duration * 2,
  ease: config.animation.easing,
};

const cutoffTransition: Transition = {
  delay: barTransition.duration - 0.3,
  duration: 0.15,
  ease: 'linear',
};

const ProgressBar: React.FC<Props> = ({
  animated,
  backgroundColor,
  className,
  minWidth = 0,
  nonZeroMinimumProgress = true,
  progressColor,
  secondaryProgressColor,
  rounded = true,
  rtl,
  size = 'medium',
  style,
  secondaryValue,
  ...props
}) => {
  const value = Math.max(0, props.value ?? 0);
  const progress = Math.min(100, value);
  const secondaryProgress = Math.min(100, secondaryValue ?? 0);
  const excessProgress = Math.max(value - 100, 0);

  const progressBarStyle = useMemo(
    () => ({
      backgroundColor,
      borderColor: progressColor,
      minWidth,
      ...style,
    }),
    [backgroundColor, minWidth, progressColor, style]
  );

  const progressBarClass = classNames(
    styles.progressBar,
    styles[`size-${size}`],
    {
      [styles.animated]: animated,
      [styles.nonZero]: nonZeroMinimumProgress && value > 0,
      [styles.rounded]: rounded,
      [styles.rtl]: rtl,
    },
    className
  );

  const transformOrigin = rtl ? 1 : 0;

  return (
    <div
      className={progressBarClass}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      style={progressBarStyle}
      {...props}
    >
      {!!secondaryProgress && (
        <motion.div
          className={classNames(styles.foreground, styles.secondary)}
          style={{
            backgroundColor: secondaryProgressColor,
            originX: transformOrigin,
            width: `${secondaryProgress}%`,
          }}
          animate={animated ? { transform: 'scaleX(1)' } : undefined}
          transition={barTransition}
        />
      )}
      <motion.div
        className={styles.foreground}
        style={{
          backgroundColor: progressColor,
          originX: transformOrigin,
          width: `${progress}%`,
        }}
        animate={animated ? { transform: 'scaleX(1)' } : undefined}
        transition={barTransition}
      />
      {excessProgress > 0 && (
        <motion.div
          className={classNames(styles.foreground, styles.excess)}
          style={{
            originX: transformOrigin,
            width: `${Math.min(excessProgress, 100)}%`,
          }}
          animate={animated ? { transform: 'scaleX(1)' } : undefined}
          transition={barTransition}
        />
      )}
      {excessProgress > 100 && (
        <motion.div
          className={styles.excessCutoff}
          animate={animated ? { opacity: 1 } : undefined}
          style={animated ? { opacity: 0 } : undefined}
          transition={cutoffTransition}
        >
          <ExcessCutoff />
          <ExcessCutoff />
        </motion.div>
      )}
    </div>
  );
};

export default ProgressBar;
